import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { Person, LocalShipping, Description, Check } from '@mui/icons-material';

import { DeliveryMethod, OrderCreationAttributes } from '../../models/Order';
import AddressForm from './AddressForm';
import DeliveryMethodForm from './DeliveryMethodForm';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Offer } from '../../models/Offer';

type Props = {
  onSubmit: (data: OrderCreationAttributes) => Promise<any>;
  deliveryCosts: { deliveryCost: number; deliveryMethod: DeliveryMethod }[];
  setDeliveryPrice: (price: number) => void;
  offer?: Offer;
  orderType?: 'standard' | 'offer';
};

function OrderForm({
  onSubmit,
  deliveryCosts,
  setDeliveryPrice,
  offer,
  orderType = 'standard',
}: Props) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<
    OrderCreationAttributes & {
      acceptTerms: boolean;
      saveAddress?: boolean;
    }
  >({
    defaultValues: {
      acceptTerms: false,
      saveAddress: false,
    },
  });

  const { user } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState<
    'personal' | 'delivery' | 'summary'
  >('personal');

  const navigate = useNavigate();

  const handleFormSubmit = async (
    data: OrderCreationAttributes & { saveAddress?: boolean },
  ) => {
    setIsSubmitting(true);
    const toastId = toast.loading('Składanie zamówienia...');

    try {
      // Wyodrębnij saveAddress przed wysłaniem danych (nie przesyłamy tego do API)
      const { saveAddress, ...orderData } = data;

      const order = await onSubmit(orderData);
      toast.update(toastId, {
        render: 'Zamówienie złożone pomyślnie',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      navigate(`${order.id}/płatność`);
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.response?.data?.message || 'Błąd składania zamówienia',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Jeśli wybrano paczkomat jako metodę dostawy
    if (watch('deliveryMethod') === DeliveryMethod.PACZKOMAT) {
      // Sprawdź czy mamy zapisaną wartość w sessionStorage
      const savedPaczkomat = sessionStorage.getItem('selectedPaczkomat');

      // Jeśli mamy zapisaną wartość i pole jest puste, przywróć wartość
      if (savedPaczkomat && !watch('paczkomat')) {
        setValue('paczkomat', savedPaczkomat, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [watch, setValue]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const sections =
    orderType === 'standard' ||
    (orderType === 'offer' &&
      offer?.deliveryMethod === DeliveryMethod.PACZKOMAT)
      ? [
          { id: 'personal', title: 'Dane osobowe', icon: <Person /> },
          { id: 'delivery', title: 'Dostawa', icon: <LocalShipping /> },
          { id: 'summary', title: 'Podsumowanie', icon: <Description /> },
        ]
      : [
          { id: 'personal', title: 'Dane osobowe', icon: <Person /> },
          { id: 'summary', title: 'Podsumowanie', icon: <Description /> },
        ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="bg-white overflow-hidden"
    >
      {/* Sekcje formularza */}
      <div className="border-b border-gray-200 bg-white">
        <div className="p-4">
          <nav className="flex space-x-4" aria-label="Tabs">
            {sections.map((section) => (
              <motion.button
                key={section.id}
                onClick={() => setActiveSection(section.id as any)}
                className={`
                  px-4 py-2 rounded-md flex items-center space-x-2 text-sm font-medium
                  ${
                    activeSection === section.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {React.cloneElement(section.icon, {
                  fontSize: 'small',
                  className: 'mr-1.5',
                })}
                {section.title}
              </motion.button>
            ))}
          </nav>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="divide-y divide-gray-200"
      >
        {/* Dane osobowe */}
        <motion.div
          className={activeSection === 'personal' ? 'block' : 'hidden'}
          variants={itemVariants}
        >
          <div className="p-2">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <Person className="mr-2" fontSize="small" />
                Dane zamawiającego
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {user?.addresses?.length
                  ? 'Wybierz adres do dostawy'
                  : 'Wprowadź dane potrzebne do realizacji zamówienia'}
              </p>
            </div>
          </div>

          <AddressForm
            control={control}
            errors={errors}
            currentUser={user}
            setValue={setValue}
            watch={watch}
          />

          <div className="p-4 flex justify-between">
            <div></div>
            <motion.button
              type="button"
              className="px-6 py-2 bg-primary text-white rounded-md shadow-sm hover:bg-primary-dark flex items-center"
              onClick={() => {
                const deliverySection = sections.find(
                  (section) => section.id === 'delivery',
                );
                if (deliverySection) {
                  setActiveSection('delivery');
                } else {
                  setActiveSection('summary');
                }
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Dalej
              <svg
                className="ml-2 h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </motion.button>
          </div>
        </motion.div>

        {/* Dostawa */}
        {(orderType === 'standard' ||
          (orderType === 'offer' &&
            offer?.deliveryMethod === DeliveryMethod.PACZKOMAT)) && (
          <motion.div
            className={activeSection === 'delivery' ? 'block' : 'hidden'}
            variants={itemVariants}
          >
            <div className="p-2">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 flex items-center">
                  <LocalShipping className="mr-2" fontSize="small" />
                  Metoda dostawy
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Wybierz preferowaną metodę dostawy zamówienia
                </p>
              </div>
            </div>

            <DeliveryMethodForm
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              deliveryCosts={deliveryCosts}
              setDeliveryPrice={setDeliveryPrice}
            />

            <div className="p-4 flex justify-between">
              <motion.button
                type="button"
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
                onClick={() => setActiveSection('personal')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg
                  className="mr-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Wróć
              </motion.button>
              <motion.button
                type="button"
                className="px-6 py-2 bg-primary text-white rounded-md shadow-sm hover:bg-primary-dark flex items-center"
                onClick={() => {
                  setActiveSection('summary');
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Dalej
                <svg
                  className="ml-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Podsumowanie */}
        <motion.div
          className={activeSection === 'summary' ? 'block' : 'hidden'}
          variants={itemVariants}
        >
          <div className="p-2">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-800 flex items-center">
                <Description className="mr-2" fontSize="small" />
                Podsumowanie zamówienia
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Sprawdź dane zamówienia przed finalizacją
              </p>
            </div>
          </div>

          <div className="p-4 space-y-6">
            {/* Regulamin */}
            <div className="flex items-start space-x-3">
              <div className="flex items-center h-5">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary rounded"
                  {...control.register('acceptTerms', { required: true })}
                />
              </div>
              <div>
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  Akceptuję{' '}
                  <a
                    href="/regulamin"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    regulamin
                  </a>{' '}
                  sklepu oraz{' '}
                  <a
                    href="/regulamin"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    politykę prywatności
                  </a>
                </label>
                {errors.acceptTerms && (
                  <p className="text-sm text-red-600 mt-1">
                    Akceptacja regulaminu jest wymagana
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-center justify-center">
              <div className="flex items-center space-x-2 text-gray-500 text-sm">
                <Check fontSize="small" className="text-green-500" />
                <span>Bezpieczne płatności</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-500 text-sm">
                <Check fontSize="small" className="text-green-500" />
                <span>Szybka wysyłka</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-500 text-sm">
                <Check fontSize="small" className="text-green-500" />
                <span>Gwarancja zwrotu</span>
              </div>
            </div>
          </div>

          <div className="p-4 flex justify-between bg-gray-50">
            <motion.button
              type="button"
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
              onClick={() => setActiveSection('delivery')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg
                className="mr-2 h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Wróć
            </motion.button>
            <motion.button
              type="submit"
              className="px-6 py-3 bg-primary text-white rounded-md shadow-sm hover:bg-primary-dark flex items-center font-medium"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            >
              {isSubmitting ? 'Przetwarzanie...' : 'Złóż zamówienie'}
              {!isSubmitting && (
                <svg
                  className="ml-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </motion.button>
          </div>
        </motion.div>
      </form>
    </motion.div>
  );
}

export default OrderForm;
